import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Form, Select, Space, Table, Tag, Typography } from "antd";
import { get } from "../../utils/http";

const { Title, Paragraph, Text } = Typography;

interface FeedbackRecord {
  id: number;
  user_id: number;
  username: string;
  user_name: string;
  feedback_type: "suggestion" | "bug" | "thought";
  content: string;
  contact?: string | null;
  app_version?: string | null;
  platform?: string | null;
  status: "pending" | "processing" | "resolved";
  created_at: string;
}

const typeMap = {
  suggestion: { text: "功能建议", color: "blue" },
  bug: { text: "问题 / Bug", color: "red" },
  thought: { text: "使用感想", color: "purple" },
};

const statusMap = {
  pending: { text: "待处理", color: "orange" },
  processing: { text: "处理中", color: "processing" },
  resolved: { text: "已处理", color: "success" },
};

function UserFeedbackPage() {
  const [dataSource, setDataSource] = useState<FeedbackRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [queryForm] = Form.useForm();

  const fetchFeedback = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const response = await get("/api/manage/user-feedback", {
        page: nextPage,
        offset: 20,
        ...queryForm.getFieldsValue(),
      });
      if (response.data.code === 0) {
        setDataSource(response.data.data.list || []);
        setTotal(response.data.data.total || 0);
        setPage(nextPage);
      }
    } finally {
      setLoading(false);
    }
  }, [queryForm]);

  useEffect(() => { fetchFeedback(1); }, [fetchFeedback]);

  const columns = [
    {
      title: "类型", dataIndex: "feedback_type", width: 120,
      render: (value: FeedbackRecord["feedback_type"]) => {
        const item = typeMap[value] || { text: value, color: "default" };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: "反馈内容", dataIndex: "content", width: 460,
      render: (value: string) => (
        <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: "展开" }} style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
          {value}
        </Paragraph>
      ),
    },
    {
      title: "用户", width: 180,
      render: (_: unknown, record: FeedbackRecord) => (
        <Space direction="vertical" size={0}>
          <Text>{record.user_name || record.username}</Text>
          <Text type="secondary">{record.username} · #{record.user_id}</Text>
        </Space>
      ),
    },
    { title: "联系方式", dataIndex: "contact", width: 180, render: (value: string | null) => value || "-" },
    {
      title: "设备", width: 140,
      render: (_: unknown, record: FeedbackRecord) => [record.platform, record.app_version].filter(Boolean).join(" / ") || "-",
    },
    {
      title: "状态", dataIndex: "status", width: 100,
      render: (value: FeedbackRecord["status"]) => {
        const item = statusMap[value] || { text: value, color: "default" };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    { title: "提交时间", dataIndex: "created_at", width: 180 },
  ];

  return (
    <Card style={{ margin: 24 }}>
      <Title level={3}>用户反馈</Title>
      <Form form={queryForm} layout="inline" style={{ marginBottom: 20 }}>
        <Form.Item name="feedback_type" label="类型">
          <Select allowClear placeholder="全部类型" style={{ width: 150 }} options={[
            { label: "功能建议", value: "suggestion" }, { label: "问题 / Bug", value: "bug" }, { label: "使用感想", value: "thought" },
          ]} />
        </Form.Item>
        <Form.Item name="status" label="状态">
          <Select allowClear placeholder="全部状态" style={{ width: 150 }} options={[
            { label: "待处理", value: "pending" }, { label: "处理中", value: "processing" }, { label: "已处理", value: "resolved" },
          ]} />
        </Form.Item>
        <Form.Item><Button type="primary" onClick={() => fetchFeedback(1)}>查询</Button></Form.Item>
        <Form.Item><Button onClick={() => { queryForm.resetFields(); fetchFeedback(1); }}>重置</Button></Form.Item>
      </Form>
      <Table<FeedbackRecord>
        rowKey="id" columns={columns} dataSource={dataSource} loading={loading} scroll={{ x: 1360 }}
        pagination={{ current: page, total, pageSize: 20, showSizeChanger: false, showTotal: value => `共 ${value} 条`, onChange: next => fetchFeedback(next) }}
      />
    </Card>
  );
}

export default UserFeedbackPage;
